import React, {useEffect, useCallback, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import {
  ButtonWithIcon,
  CardSkeletonLoader,
  ErrorBoundary
} from '@/components/atoms';
import {SaveCancelFooter, SectionTitle} from '@/components/molecules';
import {PageContainer} from '@/components/organisms';
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook';
import {handleGetActivity} from '@/services/activities';
import {useAppSelector, useAppDispatch} from '@/store';
// import { WARNING_MESSAGE } from '@/types/Activity'

import {setToastNotifications} from '@/store/slices/commonSlice';
import {getFormattedDate, getCurrencySymbol} from '@/utils/commons';
import {COLOR} from '@/utils/constants/colors';
import {Constants, localCurrencyText} from '@/utils/constants/commonConstants';
import {SIZE} from '@/utils/constants/globalStyle';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {validate} from 'uuid';
import {useErrorHandler} from '@/hooks/useErrorHandler';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import {setActivityFormData} from '@/store/slices/activitySlice';
import {getModal} from '@/utils/CommonModal';
import {ROUTES} from '@/utils/constants/routesConstants';
import {ACTIVITY_TYPE} from '@/types/Activity';
import {logger} from '@/helpers/logger';
import './ActivityReview.scss';
import SurveyActivityReview from '@/containers/Activity/ActivityReview/SurveyActivityReview';

const getExcludedKeys = (activityType) => {
  const baseExcludedKeys = ['status', 'name', 'uuid', 'id', 'allCustomers'];

  // For Currency to Points, show hasEndDate field
  if (activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS) {
    return baseExcludedKeys;
  }

  // For other activities, exclude hasEndDate
  return [...baseExcludedKeys, 'hasEndDate'];
};

const SURVEY_HIDDEN_KEYS = ['segmentExclude', 'excludedSegments'];

const formatTagsCommaSeparated = (segments, options = {}) => {
  const {
    preserveGroups = false,
    groupSeparator = ' | ',
    itemSeparator = ', ',
    emptyFallback = '-'
  } = options;

  if (!Array.isArray(segments) || segments.length === 0) return emptyFallback;

  const formatSegmentValue = (item) => {
    if (item == null) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
      return (
        item.segmentName || item.label || item.value || item.segmentId || ''
      );
    }
    return String(item);
  };

  if (preserveGroups) {
    const hasNestedGroups = segments.some(Array.isArray);
    if (hasNestedGroups) {
      return (
        segments
          .map((segmentGroup) =>
            (Array.isArray(segmentGroup) ? segmentGroup : [segmentGroup])
              .map(formatSegmentValue)
              .filter(Boolean)
              .join(itemSeparator)
          )
          .filter(Boolean)
          .join(groupSeparator) || emptyFallback
      );
    }
  }

  return (
    segments
      .flatMap((item) => (Array.isArray(item) ? item : [item]))
      .map(formatSegmentValue)
      .filter(Boolean)
      .join(itemSeparator) || emptyFallback
  );
};

const normalizeUrl = (url) => {
  if (!url || String(url).trim() === '') return '';
  return String(url).trim();
};

const ActivityReviewField = ({label, value}) => {
  let displayValue = value;
  const upperCaseLabels = [
    'Activity Type',
    'Receive Points',
    'Order Type',
    'Activity Name'
  ];
  if (upperCaseLabels.includes(label)) {
    displayValue = value?.toUpperCase();
  }
  // Replace colons with underscores in Activity Name
  if (label === 'Activity Name' && displayValue) {
    displayValue = displayValue.replace(/:/g, '_');
  }
  if (label === 'Start Date' || label === 'End Date') {
    displayValue = getFormattedDate(value, false, '-', 'yyyy-mm-dd');
  }
  // Format hasEndDate as YES/NO
  if (label === 'Has End Date') {
    displayValue = value ? 'YES' : 'NO';
  }
  // Review display: segment rows may be grouped ids or chip objects—format human-readable labels
  if (Array.isArray(displayValue)) {
    const segmentLikeLabels = [
      'Segments',
      'Selected Segments',
      'Excluded Segments'
    ];
    if (segmentLikeLabels.includes(label)) {
      displayValue = formatTagsCommaSeparated(displayValue, {
        preserveGroups: true,
        emptyFallback: ''
      });
    } else {
      displayValue = displayValue.join(', ');
    }
  }
  // Format noOfDays as "X Days" for Point Expiration activity
  if (label === 'No Of Days') {
    label = 'Frequency';
    displayValue = `${value} Days`;
  }
  return (
    <>
      {label === 'Start Date' && <SectionTitle label={'Availability'} />}
      {(label === 'Local Currency' || label === 'Points To Redeem') && (
        <SectionTitle label={'Point Allocation'} />
      )}
      {label === 'Number Of Cases' && (
        <SectionTitle label={'Point Allocation (Number Of Cases)'} />
      )}
      <div className='mt-4 flex w-full border-b border-gray-700 pb-2 md:w-1/2'>
        <div className='w-1/2'>
          <p className='pl-1 text-sm text-[#D0D0D0]'>
            {label === 'Receive Points' ? 'Frequency' : label}
          </p>
        </div>
        <div className='w-1/2'>
          <p className='pl-1 font-bold text-white'>{displayValue}</p>
        </div>
      </div>
    </>
  );
};

ActivityReviewField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
    PropTypes.object
  ])
};

const ActivityReview = ({cancelModalHandlers, isSurveyViewMode = false}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {handleApiError} = useErrorHandler();
  const {id} = useParams();
  const [searchParams] = useSearchParams();
  const reviewParam = searchParams.get('review');
  const isReviewPublished =
    reviewParam?.toLowerCase() === Constants.ReviewPublished.toLowerCase();
  const isReviewCompleted =
    reviewParam?.toLowerCase() === Constants.ReviewCompleted.toLowerCase();
  const isReviewInProgress =
    reviewParam?.toLowerCase() === Constants.InProgress.toLowerCase();

  const [loading, setLoading] = useState(false);
  const {
    getFormDataWithOrder,
    handlePublish,
    handleDraft,
    handleCancel: hookHandleCancel,
    activityFormData,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
    isNameValid,
    handleValidationModalClose
  } = useActivityReview();

  const getActivity = useCallback(
    async (id) => {
      try {
        const paramKey = validate(id) ? 'uuid' : 'id';
        setLoading(true);
        dispatch(setActivityFormData({})); // Reset form data before fetching new activity

        const response = await handleGetActivity(`${paramKey}=${id}`, false);
        setLoading(false);
        if (response?.data.success) {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Activity data fetched successfully',
              type: 'success'
            })
          );
          if (response?.data?.content?.length > 0) {
            const data = response?.data?.content[0].versionData;
            // Extract activityId from the correct location
            const formDataWithOrder = getFormDataWithOrder(data);
            dispatch(
              setActivityFormData({
                ...formDataWithOrder,
                status: data.status,
                ...(data.activityId && {activityId: data.activityId}),
                [paramKey]: id
              })
            );
          }
        } else if (response?.data) {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Activity data fetched successfully',
              type: 'success'
            })
          );
          // Extract activityId from the correct location
          const formDataWithOrder = getFormDataWithOrder(response?.data);
          dispatch(
            setActivityFormData({
              ...formDataWithOrder,
              status: response?.data.status,
              activityId: response?.data.activityId,
              [paramKey]: id
            })
          );
        } else {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Failed to fetch activity data',
              type: 'error'
            })
          );
          logger.error('Failed to fetch activity data:', response?.message);
        }
      } catch (error) {
        setLoading(false);
        logger.error('Error fetching activity data:', error);
        handleApiError(error);
      }
    },
    [dispatch, getFormDataWithOrder, handleApiError]
  );

  useEffect(() => {
    if (id) getActivity(id);
  }, [getActivity, id]);

  // Use cancel handler from props if available (for AddActivityReview page)
  // Otherwise, use the hook's handler for direct usage
  const handleCancel = cancelModalHandlers?.handleCancel || hookHandleCancel;

  const countryCode = useAppSelector((state) => state.countryCode).countryCode;
  const displayCurrency = useMemo(
    () => getCurrencySymbol(countryCode),
    [countryCode]
  );

  const isEditable = useMemo(() => {
    return (
      activityFormData.status?.toLowerCase() !==
      Constants.Completed.toLowerCase()
    );
  }, [activityFormData.status]);

  const disableDraft = useMemo(() => {
    return (
      activityFormData.status?.toLowerCase() ===
        Constants.Published.toLowerCase() ||
      activityFormData.status?.toLowerCase() ===
        Constants.InProgress.toLowerCase()
    );
  }, [activityFormData.status]);

  const isSurveyActivity =
    activityFormData?.activityType === ACTIVITY_TYPE.SURVEY;

  const surveyUrl = normalizeUrl(activityFormData?.url);
  const selectedSegmentsValue = formatTagsCommaSeparated(
    activityFormData?.selectedSegments
  );
  const excludedSegmentsValue = formatTagsCommaSeparated(
    activityFormData?.excludedSegments
  );
  const hasExcludedSegments =
    Array.isArray(activityFormData?.excludedSegments) &&
    activityFormData.excludedSegments.length > 0;
  const isExcluded =
    Boolean(activityFormData?.segmentExclude) && hasExcludedSegments;

  const editButtonSchema = {
    className: 'ml-8 flex items-center text-sm text-[#0069D7] border-none',
    title: 'Edit',
    size: SIZE.SMALL,
    variant: COLOR.TERTIARY,
    isDark: true
  };

  const getCustomModal = (type) => {
    if (type === 'NAME_VALIDATION_MODAL') {
      return getModal({
        title: 'Duplicate Name Detected',
        text: (
          <>
            It looks like there's already an Activity with this name.
            <br />
            To avoid confusion, please enter a unique name before continuing.
          </>
        ),
        showPrimaryButton: true,
        primaryButtonText: 'Edit Name',
        onClickPrimaryButton: handleValidationModalClose,
        showSecondaryButton: false,
        showModal: !isNameValid,
        onCloseModal: handleValidationModalClose,
        hideCloseButton: true
      });
    }
    return null;
  };
  return (
    <ErrorBoundary>
      <>
        {isSurveyActivity ? (
          <SurveyActivityReview
            loading={loading}
            showToast={showToast}
            toastMessage={toastMessage}
            handleToastClose={handleToastClose}
            handleViewActivity={handleViewActivity}
            isEditable={isEditable}
            editButtonSchema={editButtonSchema}
            navigate={navigate}
            activityFormData={activityFormData}
            selectedSegmentsValue={selectedSegmentsValue}
            isExcluded={isExcluded}
            excludedSegmentsValue={excludedSegmentsValue}
            surveyUrl={surveyUrl}
            getFormattedDate={getFormattedDate}
          />
        ) : (
          <PageContainer data-testid='activity-review' minHeight={'65vh'}>
            {loading && <CardSkeletonLoader count={3} />}
            {!loading && (
              <>
                <div
                  data-testid='activity-review-content'
                  className='flex items-center justify-between'>
                  <SectionTitle label={'Activity Setup'} />
                  {isEditable && (
                    <ButtonWithIcon
                      uiSchema={{
                        ...editButtonSchema
                      }}
                      iconLeading='edit'
                      handleButton={() => navigate(ROUTES.ACTIVITY_ADD)}
                    />
                  )}
                </div>
                {showToast && (
                  <DuplicateAlert
                    type='info'
                    message={toastMessage}
                    show={true}
                    onCloseClick={handleToastClose}
                    handleClick={handleViewActivity}
                    buttonText='View Activity'
                  />
                )}
                <>
                  {Object.entries(activityFormData)
                    .filter(([key]) => {
                      // Exclude internal/metadata fields
                      const excludedKeys = getExcludedKeys(
                        activityFormData?.activityType
                      );
                      return (
                        !excludedKeys.includes(key) &&
                        !SURVEY_HIDDEN_KEYS.includes(key)
                      );
                    })
                    .map(([key, value]) => {
                      const defaultLabel = key
                        .replace(/([A-Z])/g, ' $1')
                        .replace(/^./, (str) => str.toUpperCase());
                      const label =
                        key === 'localCurrencyValue' && displayCurrency
                          ? `Local Currency Value (${displayCurrency})`
                          : defaultLabel;
                      return (
                        <ActivityReviewField
                          key={key}
                          label={label}
                          value={
                            key === localCurrencyText && value
                              ? `${value} ${displayCurrency}`
                              : value || ''
                          }
                        />
                      );
                    })}
                  {!isNameValid && getCustomModal('NAME_VALIDATION_MODAL')}
                </>
              </>
            )}
            {/* <WarningLabel title={WARNING_MESSAGE.SCHEDULING_CONFLICT_DETECTED} /> */}
          </PageContainer>
        )}
        {!isSurveyViewMode &&
          !isReviewPublished &&
          !isReviewCompleted &&
          !isReviewInProgress && (
            <SaveCancelFooter
              isReview={true}
              handleCancel={handleCancel}
              nextAction={handlePublish}
              draftAction={handleDraft}
              disableDraft={disableDraft}
            />
          )}
      </>
    </ErrorBoundary>
  );
};

ActivityReview.propTypes = {
  cancelModalHandlers: PropTypes.shape({
    handleCancel: PropTypes.func
  }),
  isSurveyViewMode: PropTypes.bool
};

export default React.memo(ActivityReview);
